import { describeMutationError } from "@api/mutationErrors";
import { ClinicAuthError } from "@auth/clinicAuthClient";
import {
  Button,
  Dialog,
  FieldError,
  Input,
} from "@scribenote/brian-design-system";
import { useId, useState } from "react";

import type { ListedDisplay } from "./displaysClient";
import { usePairDisplay } from "./displaysQueries";

// Mirrors brian's `normalize_user_code` (modules/display/pairing.py).
const USER_CODE_LENGTH = 8;
const codeLetters = (raw: string) => raw.replace(/[^A-Z]/gi, "").toUpperCase();
// The hyphen appears only once a fifth letter is typed, so backspace can't get stuck on it.
const formatCode = (raw: string) => {
  const letters = codeLetters(raw).slice(0, USER_CODE_LENGTH);
  return letters.length > 4
    ? `${letters.slice(0, 4)}-${letters.slice(4)}`
    : letters;
};

const pairErrorMessage = (error: unknown): string =>
  error instanceof ClinicAuthError && error.status === 400
    ? "That code didn’t work. Enter the code the screen shows now."
    : describeMutationError(error, "pair the screen");

type PairTarget = Pick<ListedDisplay, "id" | "name"> &
  Partial<Pick<ListedDisplay, "device">>;

export const PairDisplayDialog = ({
  display,
  open,
  onClose,
  sessionKey,
}: {
  display: PairTarget;
  open: boolean;
  onClose: () => void;
  sessionKey: string;
}) => {
  const [code, setCode] = useState("");
  const formId = useId();
  const pair = usePairDisplay(sessionKey);

  const close = () => {
    pair.reset();
    setCode("");
    onClose();
  };

  const submit = () => {
    if (codeLetters(code).length !== USER_CODE_LENGTH) return;
    pair.mutate(
      { displayId: display.id, userCode: code },
      { onSuccess: close },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      size="sm"
      dismissable={!pair.isPending}
      title={`Pair ${display.name}`}
      footer={
        <>
          <Button variant="outline" onClick={close} disabled={pair.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            disabled={codeLetters(code).length !== USER_CODE_LENGTH}
            busy={pair.isPending}
            data-testid="admin-display-pair-submit"
          >
            {pair.isPending ? "Pairing" : "Pair"}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className="flex flex-col gap-3"
      >
        <Input
          label="Code on the screen"
          value={code}
          onChange={(e) => {
            pair.reset();
            setCode(formatCode(e.target.value));
          }}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          placeholder="BCDF-GHJK"
          hint={
            display.device
              ? "The screen paired now stops showing this display."
              : undefined
          }
          disabled={pair.isPending}
          className="font-mono tracking-[0.12em]"
          data-testid="admin-display-pair-code"
        />
        {pair.isError && (
          <FieldError>{pairErrorMessage(pair.error)}</FieldError>
        )}
      </form>
    </Dialog>
  );
};

export const PairDisplayButton = ({
  display,
  sessionKey,
}: {
  display: ListedDisplay;
  sessionKey: string;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        data-testid="admin-display-pair"
      >
        Pair
      </Button>
      <PairDisplayDialog
        display={display}
        open={open}
        onClose={() => setOpen(false)}
        sessionKey={sessionKey}
      />
    </>
  );
};
